<template>
    <div>
        <PageHeader :title="pageHeader" description="Crie a Matriz de Risco"></PageHeader>
        <Loading :overlay="true" overlaytext="Buscando informações... Aguarde..." :show="loading" />
        <div class="space-y-4">
            <section class="overflow-hidden rounded-[28px] border border-gray-100 bg-white shadow-sm">
                <div class="border-b border-slate-100 px-4 py-5 sm:px-6 sm:py-6">
                    <div class="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                        <div class="flex items-start gap-4">
                            <div v-if="patient.photo_url" class="h-16 w-16 shrink-0 overflow-hidden rounded-3xl ring-4 ring-white shadow-sm sm:h-20 sm:w-20">
                                <img :src="patient.photo_url" alt="" class="h-full w-full object-cover" />
                            </div>
                            <div v-else class="flex h-16 w-16 shrink-0 items-center justify-center rounded-3xl bg-slate-200 text-xl font-bold text-slate-500 ring-4 ring-white shadow-sm sm:h-20 sm:w-20 sm:text-2xl">
                                {{ initials(patient.name) }}
                            </div>

                            <div class="min-w-0">
                                <div class="flex flex-wrap items-center gap-2">
                                    <h2 class="break-words text-xl font-bold text-slate-800 sm:text-2xl">{{ patient.name }}</h2>
                                    <span class="rounded-full bg-cyan-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-700">
                                        {{ levelLabel(patient.level) }}
                                    </span>
                                </div>
                                <p class="mt-1 break-words text-sm text-slate-500">{{ patient.email || 'Sem email cadastrado' }}</p>
                                <p class="mt-1 text-xs text-slate-400">Avaliação: {{ defaultDate(record.created_at) }}</p>
                                <div class="mt-3 flex flex-wrap gap-2">
                                    <span
                                        v-for="modality in modalityLabels(patient)"
                                        :key="modality"
                                        class="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600"
                                    >
                                        {{ modality }}
                                    </span>
                                    <span v-if="!modalityLabels(patient).length" class="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-400">
                                        Sem modalidade
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div class="flex flex-wrap gap-2">
                            <BaseButton v-if="patient.uuid" skin="primary-outline-small" :to="{ name: 'patientForm', params: { uuid: patient.uuid } }">
                                <template v-slot:icon>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="mt-1 h-4 w-4 text-cyan-600">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
                                    </svg>
                                </template>
                                Editar paciente
                            </BaseButton>

                            <BaseButton v-if="patient.uuid" skin="primary-small" :to="{ name: 'records', params: { uuid: patient.uuid } }">
                                <template v-slot:icon>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="mt-1 h-5 w-5">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                                    </svg>
                                </template>
                                Ficha Completa
                            </BaseButton>
                        </div>
                    </div>
                </div>

                <details class="group border-t border-slate-100">
                    <summary class="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-5 sm:px-6">
                        <div>
                            <div class="text-sm font-semibold text-slate-800">Detalhes do paciente</div>
                            <div class="mt-1 text-xs text-slate-400">Telefone, documento, nascimento, endereço e anotações clínicas.</div>
                        </div>
                        <div class="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-500 transition-transform duration-200 group-open:rotate-180">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </summary>

                    <div class="grid gap-4 px-4 pb-6 sm:grid-cols-2 sm:px-6 lg:grid-cols-3">
                        <div class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                            <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Telefone</div>
                            <div class="mt-1 text-sm font-semibold text-slate-700">{{ patient.phone || '—' }}</div>
                        </div>
                        <div class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                            <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Documento</div>
                            <div class="mt-1 text-sm font-semibold text-slate-700">{{ patient.document || '—' }}</div>
                        </div>
                        <div class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                            <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Nascimento</div>
                            <div class="mt-1 text-sm font-semibold text-slate-700">{{ patient.birthdate ? defaultDate(patient.birthdate) : '—' }}</div>
                            <div v-if="patient.birthdate" class="mt-1 text-xs text-slate-400">{{ calculateAge(patient.birthdate) }} anos</div>
                        </div>
                        <div class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                            <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Sexo</div>
                            <div class="mt-1 text-sm font-semibold text-slate-700">{{ genderLabel(patient.gender) }}</div>
                        </div>
                        <div class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                            <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Cidade / Estado</div>
                            <div class="mt-1 text-sm font-semibold text-slate-700">{{ patient.city ? patient.city.name : '—' }}<span v-if="patient.state"> / {{ patient.state.name }}</span></div>
                        </div>
                        <div class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                            <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Endereço</div>
                            <div class="mt-1 text-sm font-semibold text-slate-700">{{ patient.address || '—' }}<span v-if="patient.zipcode"> · {{ patient.zipcode }}</span></div>
                        </div>
                        <div class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                            <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Médico</div>
                            <div class="mt-1 text-sm font-semibold text-slate-700">{{ patient.doctor || '—' }}</div>
                        </div>
                        <div class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                            <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Profissão</div>
                            <div class="mt-1 text-sm font-semibold text-slate-700">{{ patient.profession || '—' }}</div>
                        </div>
                    </div>
                </details>
            </section>

            <div class="card flex flex-col items-center justify-center gap-5 overflow-hidden p-4 sm:p-5 lg:flex-row lg:items-start">
                <div class="w-full max-w-full">
                    <div class="flex justify-center">
                        <RiskMatrix :data="matrix" :compact="true"></RiskMatrix>
                    </div>
                </div>
                <BodyRiskMap v-if="showBodyRiskMap && matrix && matrix.length" :data="matrix" :width="170"></BodyRiskMap>
            </div>
            <div class="card p-4">
                <label class="label">De acordo com a sua avaliação, quais regiões do corpo do atleta apresentam vulnerabilidades?
                    <small>(Selecione todas as regiões aplicáveis)</small>
                </label>
                <BodyPartPicker class="mt-3" v-model="matrix" :options="bodyPartOptions"></BodyPartPicker>
            </div>
        </div>
        <div v-if="matrix && matrix.length" class="card mt-4 p-4 space-y-3">
            <div v-for="bodyPart in matrix" :key="bodyPart.id" class="rounded-xl border border-gray-100 p-3 sm:p-4">
                <div class="text-sm font-semibold text-gray-700">{{ bodyPart.label }}</div>
                <div class="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <div class="mb-1 text-xs font-semibold uppercase text-gray-400">Probabilidade de ocorrer uma lesão nessa região</div>
                        <BaseButtonGroup v-model="bodyPart['probability']" :options="probabilityOptions"></BaseButtonGroup>
                    </div>
                    <div>
                        <div class="mb-1 text-xs font-semibold uppercase text-gray-400">Impacto para o atleta, se ocorrer</div>
                        <BaseButtonGroup v-model="bodyPart['impact']" :options="impactOptions"></BaseButtonGroup>
                    </div>
                </div>
            </div>
        </div>
        <div class="mt-6 flex flex-col pb-8 sm:pb-10">
            <div class="flex justify-end space-x-4 pt-2">
                <BackButton></BackButton>
                <BaseButton skin="primary" v-if="matrix && matrix.length" @click="submit()">{{ isEdit ? "Atualizar" : "Salvar" }}</BaseButton>
            </div>
        </div>
    </div>
</template>

<script>

import { ref, onMounted } from 'vue';
import { PatientService, MatrixService, RecordService } from '../services/Entity';
import UtilService from "../services/UtilService";
import { useRoute, useRouter } from 'vue-router';
import { defaultDate } from '../filters/date';
import Swal from 'sweetalert';
import { DateTime } from 'luxon';
import RiskMatrix from '../components/RiskMatrix.vue';
import BodyRiskMap from '../components/BodyRiskMap.vue';
import BodyPartPicker from '../components/BodyPartPicker.vue';
import BaseButtonGroup from '../components/forms/BaseButtonGroup.vue';
import { useToast } from 'vue-toast-notification';
import { SHOW_BODY_RISK_MAP } from '../constants/features';

export default {
    components: {
        RiskMatrix,
        BodyRiskMap,
        BodyPartPicker,
        BaseButtonGroup
    },
    setup() {
        const pageHeader = 'Matriz de Risco';
        const loading = ref(false);
        const route = useRoute();
        const router = useRouter();
        const patient = ref({});
        const showBodyRiskMap = SHOW_BODY_RISK_MAP;
        const matrix = ref();
        const record = ref({});
        const isEdit = ref(false);
        const probabilityOptions = ref([
            {
                title: 'Baixa',
                value: 'low',
                color: 'emerald'
            },
            {
                title: 'Média',
                value: 'medium',
                color: 'amber'
            },
            {
                title: 'Alta',
                value: 'high',
                color: 'orange'
            },
            {
                title: 'Muito Alta',
                value: 'very_high',
                color: 'red'
            }
        ]);
        const impactOptions = ref([
            {
                title: 'Leve',
                value: 'low',
                color: 'emerald'
            },
            {
                title: 'Médio',
                value: 'medium',
                color: 'amber'
            },
            {
                title: 'Moderado',
                value: 'high',
                color: 'orange'
            },
            {
                title: 'Severo',
                value: 'very_high',
                color: 'red'
            }
        ]);
        const bodyPartOptions = ref([]);
        const bodyPartLabels = [
            "Cabeça",
            "Pescoço",
            "Ombro direito",
            "Ombro esquerdo",
            "Cotovelo direito",
            "Cotovelo esquerdo",
            "Punho e mão direita",
            "Punho e mão esquerda",
            "Coluna cervical",
            "Coluna torácica",
            "Coluna lombar",
            "Quadril direito",
            "Quadril esquerdo",
            "Adutor direito",
            "Adutor esquerdo",
            "Coxa anterior direita",
            "Coxa anterior esquerda",
            "Coxa posterior direita",
            "Coxa posterior esquerda",
            "Joelho direito",
            "Joelho esquerdo",
            "Perna direita",
            "Perna esquerda",
            "Tornozelo direito",
            "Tornozelo esquerdo",
            "Pé direito",
            "Pé esquerdo"
        ];

        onMounted(async () => {
            mountBodyPartOptions();

            loading.value = true;
            const { patientUUID, recordUUID, matrixUUID, recordId } = handleRoute(route);

            if (!patientUUID) {
                Swal({
                    title: 'Ocorreu um problema ao consultar os dados do paciente',
                    text: 'Atualize a página e tente novamente, se o erro persistir entre em contato com o suporte',
                    icon: 'error'
                });
                return;
            }

            if (matrixUUID) {
                isEdit.value = true;
                const { data } = await MatrixService.getByUUID(matrixUUID);
                matrix.value = data.info;
            } else {
                isEdit.value = false;
                const { data } = await MatrixService.getMostRecentMatrixFromPatient(patientUUID);
                matrix.value = data.info;
            }

            await getRecord(recordId);
            await getPatientByUUID(patientUUID);

            loading.value = false;
        });

        function mountBodyPartOptions() {
            bodyPartOptions.value = bodyPartLabels.map(label => ({
                label,
                id: UtilService.generateUUID()
            }));
        }

        function handleRoute(route) {
            const patientUUID = route.params.patient_uuid;
            const recordUUID = route.params.record_uuid;
            const matrixUUID = route.params.matrix_uuid;
            const recordId = route.query.record_id;

            return {
                patientUUID: patientUUID ? patientUUID : false,
                recordUUID: recordUUID ? recordUUID : false,
                matrixUUID: matrixUUID ? matrixUUID : false,
                recordId: recordId ? recordId : false
            }
        }

        async function getRecord(recordId) {
            try {
                loading.value = true;
                const { data } = await RecordService.getById(recordId);

                record.value = data;
                loading.value = false;
            } catch(err) {
                loading.value = false;
                console.log(err);

                Swal({
                    title: 'Ocorreu um problema ao consultar os dados do paciente',
                    text: 'Atualize a página e tente novamente, se o erro persistir entre em contato com o suporte',
                    icon: 'error'
                });
            }
        }

        async function submit() {
            if (matrix.value && matrix.value.length === 0) {
                Swal({
                    title: 'Selecione pelo menos uma parte do corpo',
                    icon: 'info'
                });
                return;
            }
            
            if (!isEdit.value) {
                await save();
            } else {
                await update();
            }
        }

        async function save() {
            try {
                loading.value = true;
                const { patientUUID, recordUUID } = handleRoute(route);

                const createdMatrix = await MatrixService.create({
                    info: matrix.value,
                    record_uuid: recordUUID,
                    patient_uuid: patientUUID
                });

                const $toast = useToast();
                $toast.open({ position: 'top-right', message: 'Informações Salvas', type: 'success' });
                router.push({
                    path: `${route.path}/${createdMatrix.data.uuid}`
                });
                isEdit.value = true;

                loading.value = false;
            } catch(err) {
                console.log(err);
                loading.value = false;
                Swal({
                    title: 'Ocorreu um problema ao salvar a Matriz de Risco',
                    text: 'Atualize a página e tente novamente, se o erro persistir entre em contato com o suporte',
                    icon: 'error'
                });
            }
        }

        async function update() {
            try {
                loading.value = true;
                const { matrixUUID } = handleRoute(route);

                await MatrixService.update({
                    info: matrix.value,
                    uuid: matrixUUID
                });

                const $toast = useToast();
                $toast.open({ position: 'top-right', message: 'Informações Atualizadas', type: 'success' });

                loading.value = false;
            } catch(err) {
                console.log(err);
                loading.value = false;

                Swal({
                    title: 'Ocorreu um problema ao atualizar a Matriz de Risco',
                    text: 'Atualize a página e tente novamente, se o erro persistir entre em contato com o suporte',
                    icon: 'error'
                });
            }
        }

        async function getPatientByUUID(uuid) {
            try {
                loading.value = true;
                const { data } = await PatientService.getByUUID(uuid);

                patient.value = data;
                loading.value = false;
            } catch(err) {
                loading.value = false;
                console.log(err);

                Swal({
                    title: 'Ocorreu um problema ao consultar os dados do paciente',
                    text: 'Atualize a página e tente novamente, se o erro persistir entre em contato com o suporte',
                    icon: 'error'
                });
            }
        }

        function calculateAge(birthdate) {
            const diff = DateTime.fromISO(birthdate, 'yyyy-MM-dd').diffNow('year');
            return Math.trunc(Math.abs(diff.years));
        }

        function initials(name) {
            if (!name) return '?';
            const parts = name.trim().split(' ').filter(Boolean);
            if (!parts.length) return '?';
            if (parts.length === 1) return parts[0][0].toUpperCase();
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }

        function modalityLabels(currentPatient) {
            return currentPatient && currentPatient.modality && currentPatient.modality.length
                ? currentPatient.modality.map((item) => item.label || item.name).filter(Boolean)
                : [];
        }

        function levelLabel(level) {
            if (level === 'high_professional') return 'Alto rendimento profissional';
            if (level === 'high_fun') return 'Alto rendimento recreacional';
            if (level === 'casual') return 'Atleta de fim de semana';
            return 'Nível não informado';
        }

        function genderLabel(gender) {
            if (gender === 'female') return 'Feminino';
            if (gender === 'male') return 'Masculino';
            return '—';
        }

        return {
            submit,
            pageHeader,
            patient,
            calculateAge,
            initials,
            modalityLabels,
            levelLabel,
            genderLabel,
            loading,
            showBodyRiskMap,
            matrix,
            bodyPartOptions,
            probabilityOptions,
            impactOptions,
            isEdit,
            defaultDate,
            record
        }
    },
}
</script>
